import { useState } from "react";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useI18n } from "@/context/I18n";
import { X, ChevronUp, ChevronDown, Trash2, Music2, Play, Pause, GripVertical, Radio } from "lucide-react";

export default function QueuePanel({ onClose }) {
  const { queue, index, currentTrack, isPlaying, radio, togglePlay, jumpTo, moveInQueue, removeFromQueue } = useMusicPlayer();
  const { t } = useI18n();
  const [dragIndex, setDragIndex] = useState(null);
  const upcoming = queue.map((t, i) => ({ t, i })).filter(({ i }) => i > index);
  const firstUpcoming = index + 1;

  const onDrop = (targetIndex) => {
    if (dragIndex != null && dragIndex !== targetIndex) moveInQueue(dragIndex, targetIndex);
    setDragIndex(null);
  };

  return (
    <div
      data-testid="queue-panel"
      className="fixed bottom-[96px] right-4 z-50 w-96 max-w-[calc(100vw-2rem)] max-h-[62vh] bg-[#14141a] border border-white/10 rounded-xl shadow-2xl flex flex-col overflow-hidden animate-fade-up"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h3 className="font-heading font-semibold flex items-center gap-2">
          {t("queue")}
          {radio && <span data-testid="queue-radio-badge" className="text-[10px] uppercase tracking-wider bg-[#FF2A4D] text-white px-1.5 py-0.5 rounded-full flex items-center gap-1"><Radio className="w-3 h-3" /> {t("radio")}</span>}
        </h3>
        <button data-testid="queue-close" onClick={onClose} className="text-zinc-400 hover:text-white p-1 rounded-md hover:bg-white/10">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-y-auto p-2">
        {currentTrack && (
          <>
            <p className="text-xs text-zinc-500 px-2 py-1">{t("now_playing")}</p>
            <div className="flex items-center gap-3 px-2 py-2 rounded-md bg-white/5 mb-2">
              {currentTrack.thumbnail ? <img src={currentTrack.thumbnail} className="w-10 h-10 rounded object-cover" alt="" /> : <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center"><Music2 className="w-4 h-4 text-zinc-500" /></div>}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate text-[#FF2A4D]">{currentTrack.title}</p>
                <p className="text-xs text-zinc-400 truncate">{currentTrack.artist}</p>
              </div>
              <button onClick={togglePlay} className="text-white p-1.5">
                {isPlaying ? <Pause className="w-4 h-4" fill="white" /> : <Play className="w-4 h-4" fill="white" />}
              </button>
            </div>
          </>
        )}

        <p className="text-xs text-zinc-500 px-2 py-1">Up next · drag to reorder</p>
        {upcoming.length === 0 ? (
          <p className="text-xs text-zinc-500 px-2 py-4 text-center">Nothing else in the queue.</p>
        ) : (
          upcoming.map(({ t, i }) => (
            <div
              key={`${t.id}-${i}`}
              data-testid={`queue-item-${i}`}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
              className={`group flex items-center gap-1 px-2 py-2 rounded-md hover:bg-white/5 ${dragIndex === i ? "opacity-40" : ""}`}
            >
              <GripVertical className="w-4 h-4 text-zinc-600 cursor-grab shrink-0" />
              <div className="min-w-0 flex-1 cursor-pointer" onClick={() => jumpTo(i)} data-testid={`queue-jump-${i}`}>
                <p className="text-sm truncate">{t.title}</p>
                <p className="text-xs text-zinc-400 truncate">{t.artist}</p>
              </div>
              <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <button data-testid={`queue-up-${i}`} onClick={() => moveInQueue(i, Math.max(firstUpcoming, i - 1))} disabled={i === firstUpcoming} className="p-1 text-zinc-400 hover:text-white disabled:opacity-20">
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button data-testid={`queue-down-${i}`} onClick={() => moveInQueue(i, Math.min(queue.length - 1, i + 1))} disabled={i === queue.length - 1} className="p-1 text-zinc-400 hover:text-white disabled:opacity-20">
                  <ChevronDown className="w-4 h-4" />
                </button>
                <button data-testid={`queue-remove-${i}`} onClick={() => removeFromQueue(i)} className="p-1 text-zinc-400 hover:text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

